/**
 * Los topes que el backend le pone a cada PANEL y a la hoja, para poder avisarlos ANTES de
 * mandar el render.
 *
 * Los límites de `reports/services/layout_schema.py` son POR PANEL: caen sobre las columnas de
 * CADA tabla (`fieldsOverflow`), no sobre la paleta de la hoja, que tiene el suyo propio y mucho
 * más alto (`layoutFieldsOverflow`, `MAX_FIELDS_PER_LAYOUT`). Hasta el 2026-09-09 eran lo mismo
 * porque toda tabla mostraba la paleta entera; desde la card hwuk9PW7 cada tabla se recorta
 * sola. Al superarlos el render devuelve 422 `INVALID_PANEL` y la hoja no se calcula.
 *
 * ⚠️ Son un ESPEJO de las constantes del backend (`MAX_METRICS_PER_PANEL`,
 * `MAX_DIMENSIONS_PER_PANEL`): no hay endpoint que las publique. Si allá cambian, acá también —
 * y ya pasó: el 2026-09-07 el backend subió a 40/10 para que las plantillas de la Demo («Meta -
 * País y Ubicación» tiene 24 métricas) revalidaran, y estas dos constantes quedaron avisando de un
 * tope que ya no existía. Los dos repos tienen que salir JUNTOS (review C-editor-01).
 * Por eso esto AVISA y no bloquea — la autoridad sigue siendo el backend, y su motivo real se
 * muestra tal cual (ver `renderError.js`). Un aviso que se adelanta de más es molesto; un bloqueo
 * que se adelanta de más deja al usuario sin poder aplicar algo que era válido.
 *
 * Por qué existe: la plantilla «Meta - Anuncios» de la Demo tiene 24 campos y es anterior al tope
 * (entró el 05/09 a las 22:24, después de la migración). Pablo la hizo cargar deseleccionando
 * métricas a ciegas — la pantalla no decía ni que había un límite ni cuál era.
 */
import {FIELD_KINDS} from './informeModel';

export const MAX_METRICS_PER_PANEL = 40;
export const MAX_DIMENSIONS_PER_PANEL = 10;
export const MAX_FIELDS_PER_LAYOUT = 120;

/** Los topes de UN panel (hoy: una tabla) sobre las columnas que muestra. */
export function fieldsOverflow(fields) {
    const list = fields || [];
    const metrics = list.filter((f) => f.kind === FIELD_KINDS.METRIC).length;
    const dimensions = list.filter((f) => f.kind === FIELD_KINDS.DIMENSION).length;
    return {
        metrics: {count: metrics, max: MAX_METRICS_PER_PANEL, over: metrics > MAX_METRICS_PER_PANEL},
        dimensions: {count: dimensions, max: MAX_DIMENSIONS_PER_PANEL, over: dimensions > MAX_DIMENSIONS_PER_PANEL},
        hasOverflow: metrics > MAX_METRICS_PER_PANEL || dimensions > MAX_DIMENSIONS_PER_PANEL,
    };
}

/**
 * El tope de la PALETA de la hoja (`MAX_FIELDS_PER_LAYOUT`): métricas + dimensiones juntas. Es
 * otro límite y otro error del backend (`INVALID_LAYOUT`, no `INVALID_PANEL`), así que no se
 * mezcla con el de la tabla.
 */
export function layoutFieldsOverflow(fields) {
    const count = (fields || []).length;
    return {count, max: MAX_FIELDS_PER_LAYOUT, hasOverflow: count > MAX_FIELDS_PER_LAYOUT};
}

/**
 * El peor desborde entre las TABLAS de la hoja: es lo que puede avisar el folio, que ya no tiene
 * una única tabla que describir.
 */
export function worstTableOverflow(panels) {
    return (panels || [])
        .filter((p) => p.type === 'table')
        .map((p) => fieldsOverflow([
            ...(p.dimensions || []).map((name) => ({name, kind: FIELD_KINDS.DIMENSION})),
            ...(p.metrics || []).map((name) => ({name, kind: FIELD_KINDS.METRIC})),
        ]))
        .reduce((worst, next) => (next.hasOverflow ? next : worst), fieldsOverflow([]));
}
